import React from 'react';
import { connect } from 'react-redux';
import { Link, RouteComponentProps } from 'react-router-dom';
import { ThunkDispatch } from 'redux-thunk';
import { PricingApi } from '../../../../api/api.apiclient';
import { DATE_FORMAT, DATE_TIME_FORMAT, StatusType } from '../../../../api/api.types';
import { UserModel } from '../../../../api/authentication/authentication.types';
import {
  CarClassModel,
  CarModelModel,
  DeskGroupModel,
  ProductModel,
  RateAdjustmentManagementType
} from '../../../../api/core/core.types';
import {
  CoreCacheStatus,
  PriceCalculatorCacheStatus,
  RatePlanListAllModel,
  RatePlanModel,
  RatePlanPatchRequest,
  RatePlanPeriodModel,
  RateType
} from '../../../../api/pricing/pricing.types';
import CacheStatusLabel from '../../../../components/forms/CacheStatusLabel';
import DropDownWithLabel from '../../../../components/forms/DropDownWithLabel';
import ConfirmationModal from '../../../../components/modals/ConfirmationModal';
import RatePlanDurationModal from '../../../../components/modals/RatePlanDurationModal';
import { RatePlanDetailTableView } from '../../../../components/ratecomponents/RatePlanDetailTableView';
import { getSurroundingRatePlans } from '../../../../helpers/rateplan.helper';
import { getTimeBeforePublish } from '../../../../helpers/timeHelper';
import { AppState } from '../../../../store/store.types';
import { STORED_ACRISS_CODES, STORED_DESK_GROUP_ID, STORED_PRODUCT_ID } from '../../../../types/app.index';
import { getRatePlanPeriods } from '../../../core/actions/rateplanperiod.actions';
import { getCarModelsList } from '../../../setup/actions/carmodel.actions';
import { getDeskGroupList } from '../../../setup/actions/deskgroup.actions';
import { getProductList } from '../../../setup/actions/product.actions';
import { getCoreCacheStatus, getRateCacheStatus } from '../../actions/ratecachestatus.actions';
import {
  deleteRatePlan,
  getRatePlan,
  getRatePlanList,
  getRatePlansView,
  patchRatePlan,
  saveAcrissCodes,
  saveDeskGroupId,
  saveProductId
} from '../../actions/rateplan.actions';
import { RatePlanActionTypes } from '../../types/rateplan.types';
import dayjs from 'dayjs';
import { WithTranslation, withTranslation } from 'react-i18next';
import RatesTimeline from '../../../../components/ratecomponents/RatesTimeline';
import { getAllCarClasses } from '../../../core/actions/carClass.actions';

interface RatePlanProps extends RouteComponentProps, WithTranslation {
  getRatePlanList: (productId?: number, deskGroupId?: number, carClasses?: string[]) => void;
  getProductList: () => void;
  getDeskGroupList: () => void;
  getCarModelList: () => void;
  getAllCarClasses: () => void;
  getRatePlanPeriodsList: () => void;
  getOneRatePlan: (id: number) => void;
  deleteRatePlan: (id: number) => void;
  patchRatePlan: (id: number, request: RatePlanPatchRequest) => void;
  saveProductId: (id: number) => void;
  saveDeskGroupId: (id: number) => void;
  saveAcrissCodes: (acrissCodes: string[]) => void;
  getRatePlansView: (coreProductId?: number) => void;
  getRateCacheStatus: () => void;
  getCoreCacheStatus: () => void;
  ratePlanPeriods: RatePlanPeriodModel[];
  ratePlans: RatePlanListAllModel[];
  products: ProductModel[];
  deskGroups: DeskGroupModel[];
  carModels: CarModelModel[];
  user: UserModel | null;
  carClasses: CarClassModel[];
  selectedRatePlan: RatePlanModel | null;
  storedProductId: number | null;
  storedDeskGroupId: number | null;
  storedAcrissCodes: string[] | null;
  rateCacheStatus: PriceCalculatorCacheStatus | null;
  coreCacheStatus: CoreCacheStatus | null;
}

interface RatePlanState {
  selectedRatePlan: RatePlanModel | undefined;
  precedingPlan: RatePlanListAllModel | null;
  followingPlan: RatePlanListAllModel | null;
  isRateTableOpen: boolean;
  isDeleteModalOpen: boolean;
  isDurationAdjustmentModalOpen: boolean;
  isConfirmDisableModalOpen: boolean;
  selectedDeskGroup: { value: string; label: string } | undefined;
  selectedProduct: ProductModel | undefined;
  selectedAcrissCodes: string[] | [];
  selectedPlanValidFrom: string | undefined;
  selectedPlanValidTo: string | undefined;
}

const MONTHS_IN_TIMELINE = 24;

class RatePlans extends React.PureComponent<RatePlanProps, RatePlanState> {

  readonly state = {
    selectedRatePlan: undefined,
    isRateTableOpen: false,
    isDeleteModalOpen: false,
    isDurationAdjustmentModalOpen: false,
    isConfirmDisableModalOpen: false,
    precedingPlan: null,
    followingPlan: null,
    selectedDeskGroup: undefined,
    selectedProduct: undefined,
    selectedPlanValidFrom: undefined,
    selectedPlanValidTo: undefined,
    selectedAcrissCodes: [] as string[]
  };

  async componentDidMount() {
    await this.getDeskGroups();
    await this.getProducts();
    await this.getAcrissCodes();

    this.loadProducts();
    this.loadDeskGroups();
    this.loadAcrissCodes();

    this.props.getCarModelList();

    if (!this.props.ratePlanPeriods.length) {
      this.props.getRatePlanPeriodsList();
    }

    this.props.getRateCacheStatus();
    this.props.getCoreCacheStatus();
  }

  componentDidUpdate(prevProps: RatePlanProps) {
    if (this.props.products !== prevProps.products) {
      this.loadProducts();
    }

    if (this.props.deskGroups !== prevProps.deskGroups) {
      this.loadDeskGroups();
    }

    if (this.props.carClasses != prevProps.carClasses) {
      this.loadAcrissCodes();
    }

    if (!this.props.carModels.length) {
      this.props.getCarModelList();
    }

  }

  storeSelectedProduct = (id: number) => {
    this.props.saveProductId(id);
  };

  storeSelectedDeskGroup = (id: number) => {
    this.props.saveDeskGroupId(id);
  };

  storeSelectedAcrissCodes = (acrissCodes: string[]) => {
    this.props.saveAcrissCodes(acrissCodes);
  };

  onTimelineObjectClick = async (id: number) => {
    await this.getOneRatePlan(id);
    if (this.props.selectedRatePlan) this.setState({
      selectedRatePlan: this.props.selectedRatePlan,
      isRateTableOpen: true
    });
  };

  getOneRatePlan = async (id: number) => this.props.getOneRatePlan(id);

  getProducts = async () => this.props.getProductList();

  deleteRatePlan = async (id: number) => this.props.deleteRatePlan(id);

  getRatePlanList = async (productId?: number, deskGroupId?: number, carClasses?: string[]) => this.props.getRatePlanList(productId, deskGroupId, carClasses);

  getDeskGroups = async () => this.props.getDeskGroupList();

  getAcrissCodes = async () => this.props.getAllCarClasses();

  loadProducts = async () => {
    if (this.props.products.length) {

      if (this.props.storedProductId) {
        const product = this.props.products.find((p) => p.id === this.props.storedProductId);

        if (product) {
          this.selectProduct(product.id);
          return;
        }

      } else {
        const storedProductId = localStorage.getItem(STORED_PRODUCT_ID);
        if (storedProductId) {
          const productId = JSON.parse(storedProductId) as number;

          if (productId) {
            const product = this.props.products.find((p) => p.id === productId);

            if (product) {
              this.selectProduct(product.id);
              return;
            }
          }
        }
      }

      this.selectProduct(this.props.products[ 0 ].id);
    }
  };

  loadDeskGroups = async () => {
    if (this.props.deskGroups.length) {

      if (this.props.storedDeskGroupId) {
        const deskGroup = this.props.deskGroups.find((dg) => dg.id === this.props.storedDeskGroupId);

        if (deskGroup) {
          this.setState({ selectedDeskGroup: { value: String(deskGroup.id), label: deskGroup.name } });
        }

      } else {
        const storedDeskGroupId = localStorage.getItem(STORED_DESK_GROUP_ID);
        if (storedDeskGroupId) {
          const deskGroupId = JSON.parse(storedDeskGroupId) as number;

          if (deskGroupId) {
            const deskGroup = this.props.deskGroups.find((dg) => dg.id === deskGroupId);

            if (deskGroup) {
              this.setState({ selectedDeskGroup: { value: String(deskGroup.id), label: deskGroup.name } });
            }
          }
        }
      }
    }
  };

  loadAcrissCodes = async () => {
    if (this.props.carClasses.length) {

      if (this.props.storedAcrissCodes) {
        const selectedAcrissCodes = this.props.carClasses.filter((carClass) => this.props.storedAcrissCodes?.includes(carClass.acrissCode));
        this.setState({ selectedAcrissCodes: selectedAcrissCodes.map(carClass => carClass.acrissCode) });

      } else {
        const storedAcrissCodes = localStorage.getItem(STORED_ACRISS_CODES);

        if (storedAcrissCodes) {
          const acrissCodes = JSON.parse(storedAcrissCodes) as string[];
          const selectedAcrissCodes = this.props.carClasses.filter((carClass) => acrissCodes?.includes(carClass.acrissCode));
          this.setState({ selectedAcrissCodes: selectedAcrissCodes.map(carClass => carClass.acrissCode) });
        }
      }
    }
  };

  openDefaultPlan = (deskGroupName?: string) => {
    if (this.props.selectedRatePlan && !deskGroupName) {
      if (this.props.ratePlans.find((rp) => rp.pickupDeskGroupId === this.props.storedDeskGroupId)) {
        this.onTimelineObjectClick(this.props.selectedRatePlan.id);
      }

    } else if (this.props.ratePlans && this.state.selectedDeskGroup) {
      if (this.props.ratePlans) {
        let firstPlan: RatePlanListAllModel | undefined;
        this.props.ratePlans.forEach((element: RatePlanListAllModel) => {
          if (firstPlan && element.rateType === RateType.BASE_RATE && dayjs(firstPlan.validTo).isAfter(dayjs(element.validFrom))) firstPlan = element;
          else if (!firstPlan && element.rateType === RateType.BASE_RATE) firstPlan = element;
        });

        if (firstPlan) this.onTimelineObjectClick(firstPlan.id);
      }
    }
  };

  openPlan = (id: number) => {
    const ratePlan = this.props.ratePlans.find((rp) => rp.id === id);

    if (ratePlan) this.onTimelineObjectClick(ratePlan.id);
    else this.openDefaultPlan();
  };

  onPlansAltered = async () => {
    const selectedProduct = this.state.selectedProduct as unknown as ProductModel;
    const selectedDeskGroup = this.state.selectedDeskGroup as unknown as { value: string; label: string };

    let deskGroupId = this.props.deskGroups[ 0 ].id;
    if (selectedDeskGroup) {
      deskGroupId = Number(selectedDeskGroup.value);
    }

    if (selectedProduct) {
      await this.getRatePlanList(Number(selectedProduct.id), Number(deskGroupId));
    }
  };

  selectProduct = async (productId: number) => {
    const selectedDeskGroup = this.state.selectedDeskGroup as unknown as { value: string; label: string };
    const selectedAcrissCodes = this.state.selectedAcrissCodes;
    let deskGroupId = this.props.deskGroups[ 0 ].id;
    if (selectedDeskGroup) {
      deskGroupId = Number(selectedDeskGroup.value);
    }

    await this.getRatePlanList(productId, Number(deskGroupId), this.state.selectedAcrissCodes);
    const product = this.props.products.find((p) => p.id === productId);

    if (product) {
      this.setState({ selectedProduct: product });
      this.storeSelectedProduct(product.id);
      this.onProductLoad();
    }
  };

  onProductLoad = (isUserAction?: boolean) => {
    if (isUserAction || !this.state.selectedDeskGroup) {
      const deskGroup = this.getSelectedDeskGroup();

      let selectedGroup;

      if (deskGroup) {
        selectedGroup = { value: String(deskGroup.id), label: deskGroup.name };
        if (deskGroup.id) {
          this.storeSelectedDeskGroup(deskGroup.id);
        }
      }

      this.setState({ isRateTableOpen: false, selectedDeskGroup: selectedGroup });
      this.openDefaultPlan(deskGroup ? deskGroup.name : undefined);

    } else {
      this.setState({ isRateTableOpen: false });
      this.openDefaultPlan();
    }
  };

  getPlansToBeRendered = () => {
    if (this.state.selectedDeskGroup) {
      const deskGroup = this.state.selectedDeskGroup as unknown as { value: string; label: string };

      if (this.props.ratePlans.length) {
        const ratePlansByDeskGroup = new Map([
          [ deskGroup.label, this.props.ratePlans ] ]);

        return ratePlansByDeskGroup;
      }
    }

    const { t } = this.props;

    return new Map([
      [ t('text.rate_plans.no_plans_found'), [] ] ]);
  };

  getEndDate = () => {
    const defaultEndDate = dayjs().startOf('day').add(MONTHS_IN_TIMELINE, 'months').format('YYYY-MM');
    if (this.props.ratePlans.length) {
      const plans: RatePlanListAllModel[] = this.props.ratePlans;

      if (plans && plans.length) {
        const endDateFromPlans = dayjs(plans[ plans.length - 1 ].validTo).startOf('day').add(2, 'months').format('YYYY-MM');

        if (dayjs(endDateFromPlans).isAfter(dayjs(defaultEndDate))) return endDateFromPlans;
      }
    }

    return defaultEndDate;
  };

  onDeleteConfirm = async () => {
    if (this.props.selectedRatePlan) {
      const selectedPlan = this.props.selectedRatePlan;
      const selectedPlanValidFrom = this.props.selectedRatePlan.validFrom;
      const selectedPlanValidTo = this.props.selectedRatePlan.validTo;
      await this.deleteRatePlan(selectedPlan.id);

      if (selectedPlan.rateType === RateType.BASE_RATE) {
        const surroundingPlans = getSurroundingRatePlans(this.props.ratePlans, selectedPlan.validFrom, selectedPlan.id, selectedPlan.pickupDeskGroupId, selectedPlan.coreProductId);

        if (surroundingPlans.followingPlan || surroundingPlans.precedingPlan) {
          const followingPlan = surroundingPlans.followingPlan ?? null;
          const precedingPlan = surroundingPlans.precedingPlan ?? null;

          this.setState({
            isDurationAdjustmentModalOpen: true,
            followingPlan,
            precedingPlan,
            isDeleteModalOpen: false,
            selectedPlanValidFrom,
            selectedPlanValidTo
          });

        } else {
          this.setState({ selectedRatePlan: undefined, isRateTableOpen: false, isDeleteModalOpen: false });

          await this.onPlansAltered();
          this.openDefaultPlan();
        }

      } else {
        this.setState({ selectedRatePlan: undefined, isRateTableOpen: false, isDeleteModalOpen: false });

        await this.onPlansAltered();
        this.openDefaultPlan();
      }
    }
  };

  onDisableConfirm = async () => {
    if (this.props.selectedRatePlan) {
      const selectedPlan = this.props.selectedRatePlan;
      const patchRequest: RatePlanPatchRequest = { statusType: StatusType.INACTIVE };

      await this.patchRatePlan(selectedPlan.id, patchRequest);
      this.setState({ isConfirmDisableModalOpen: false, selectedRatePlan: undefined, isRateTableOpen: false });
      await this.onPlansAltered();
      this.openPlan(selectedPlan.id);
    }
  };

  patchRatePlan = async (id: number, request: RatePlanPatchRequest) => this.props.patchRatePlan(id, request);

  onPrecedingPlanExtend = async (id: number) => {
    this.setState({
      isDurationAdjustmentModalOpen: false
    });

    if (this.state.selectedPlanValidTo) {
      const patchRequest: RatePlanPatchRequest = {
        validTo: this.state.selectedPlanValidTo
      };

      this.setState({
        selectedRatePlan: undefined,
        followingPlan: null,
        precedingPlan: null,
        selectedPlanValidTo: undefined,
        selectedPlanValidFrom: undefined
      });

      await this.patchRatePlan(id, patchRequest);

      await this.onPlansAltered();
    }

    this.openDefaultPlan();
  };

  onFollowingPlanExtend = async (id: number) => {
    this.setState({
      isDurationAdjustmentModalOpen: false
    });

    if (this.state.selectedPlanValidFrom) {
      const patchRequest: RatePlanPatchRequest = {
        validFrom: this.state.selectedPlanValidFrom
      };

      this.setState({
        selectedRatePlan: undefined,
        followingPlan: null,
        precedingPlan: null,
        isRateTableOpen: false,
        selectedPlanValidTo: undefined,
        selectedPlanValidFrom: undefined
      });

      await this.patchRatePlan(id, patchRequest);

      await this.onPlansAltered();
    }

    this.openDefaultPlan();
  };

  activateRatePlanClicked = async () => {
    if (this.props.selectedRatePlan) {
      const selectedPlan = this.props.selectedRatePlan;
      const patchRequest: RatePlanPatchRequest = { statusType: StatusType.ACTIVE };

      await this.patchRatePlan(selectedPlan.id, patchRequest);
      this.setState({ isConfirmDisableModalOpen: false, selectedRatePlan: undefined, isRateTableOpen: false });
      await this.onPlansAltered();
      this.openPlan(selectedPlan.id);
    }
  };


  getSelectedDeskGroup = () => {
    if (this.props.deskGroups.length) {
      const selectedDeskGroup = this.state.selectedDeskGroup as unknown as { value: string; label: string };

      if (selectedDeskGroup) {
        const deskGroup = this.props.deskGroups.find((dg) => dg.id === Number(selectedDeskGroup.value));
        if (deskGroup) return deskGroup;
      }

      return this.props.deskGroups[ 0 ];
    }

    return undefined;
  };

  handleProductChange = async (value?: string) => {
    const product = this.props.products.find((p) => p.id === Number(value));

    if (product) {
      this.setState({ selectedProduct: product });
      this.storeSelectedProduct(Number(value));

      const deskGroup = this.getSelectedDeskGroup();
      const carClasses = this.state.selectedAcrissCodes;
      if (deskGroup) await this.getRatePlanList(Number(value), Number(deskGroup.id), carClasses);

      this.onProductLoad(true);
    }
  };

  getProduct = () => {
    if (this.props.products.length) {
      const selectedProduct = this.state.selectedProduct as unknown as ProductModel;

      const product = this.props.products.find((p) => p.id === Number(selectedProduct.id));
      if (product) return product;

      return this.props.products[ 0 ];
    }

    return undefined;
  };

  handleDeskGroupChange = async (value?: string) => {
    const deskGroup = this.props.deskGroups.find((dg) => dg.id === Number(value));

    if (deskGroup) {
      this.setState({ selectedDeskGroup: { label: String(deskGroup.name), value: String(deskGroup.id) } });
      this.storeSelectedDeskGroup(Number(value));

      const product = this.getProduct();
      const carClasses = this.state.selectedAcrissCodes;
      if (product) await this.getRatePlanList(product.id, deskGroup?.id ?? Number(deskGroup.id), carClasses);

      this.openDefaultPlan(deskGroup.name);

      if (!this.props.ratePlans.length) {
        this.setState({ selectedRatePlan: undefined, isRateTableOpen: false });
      }
    }
  };

  handleCarClassChange = async () => {
    const selectedValues = this.state.selectedAcrissCodes.filter(c => c !== undefined && c !== null && c !== '');

    const product = this.getProduct();
    const deskGroup = this.getSelectedDeskGroup();

    this.storeSelectedAcrissCodes(selectedValues);
    if (product) {
      await this.getRatePlanList(product.id, deskGroup ? Number(deskGroup.id) : undefined, selectedValues);
    }

    if (deskGroup) {
      this.openDefaultPlan(deskGroup.name);
    }

    if (!this.props.ratePlans.length) {
      this.setState({ selectedRatePlan: undefined, isRateTableOpen: false });
    }
  };

  onCarClassClear = async () => {
    const product = this.getProduct();
    const deskGroup = this.getSelectedDeskGroup();
    const selectedAcrissCodes: string[] = [];
    this.storeSelectedAcrissCodes(selectedAcrissCodes);
    this.setState({ selectedAcrissCodes: selectedAcrissCodes });
    if (product) {
      await this.getRatePlanList(product.id, deskGroup ? Number(deskGroup.id) : undefined, selectedAcrissCodes);
    }
    if (deskGroup) {
      this.openDefaultPlan(deskGroup.name);
    }

    if (!this.props.ratePlans.length) {
      this.setState({ selectedRatePlan: undefined, isRateTableOpen: false });
    }
  };

  onPublish = async () => {
    await PricingApi.RatePlan.publishRatePlans();
    this.props.getRateCacheStatus();
    this.props.getCoreCacheStatus();
  };

  getSelectedProductDropDownValue = (selectedProduct: ProductModel) => {
    if (selectedProduct) {
      return { value: String(selectedProduct.id), label: selectedProduct.name };
    }
  };

  getAdjustmentButton = (selectedProduct: ProductModel) => {
    if (selectedProduct && selectedProduct.rateAdjustmentManagementType === RateAdjustmentManagementType.MANUAL) {
      return (
        <Link
          to={{ pathname: `/rates/adjustment/${this.props.selectedRatePlan ? this.props.selectedRatePlan.id : '0'}/create` }}
          className="btn btn-primary btn-wide ml-1 mr-2"
        >
          {this.props.t('b.rate_plans.add_adjustment').toUpperCase()}
        </Link>
      );
    }
  };

  render() {
    const selectedProduct = this.state.selectedProduct as unknown as ProductModel;
    const { t } = this.props;

    return (
      <main>
        <div className="page-content">
          <div className="d-flex align-items-center justify-content-between mt-2 mb-2 mr-1">
            <h1 className="rates-h1">{t('page_title.rate_plans')}</h1>
            <div>
              {this.props.selectedRatePlan &&
                this.props.selectedRatePlan.rateType !== RateType.ADJUSTMENT && (
                  this.getAdjustmentButton(selectedProduct)
                )}
              <Link
                to={{
                  pathname: '/rates/standardrate/create'
                }}
                className="btn btn-primary end-of-row"
              >
                {t('b.rate_plans.add_rate').toUpperCase()}
              </Link>
            </div>
          </div>
          <hr className="mt-0 mb-0"/>
          <div className="d-flex justify-content-between mb-2 mt-2">
            <div className="d-flex justify-content-start align-items-center">
              <div className="row d-flex align-items-center ml-2 rates-dropdown-element-container">
                <DropDownWithLabel
                  // eslint-disable-next-line no-nested-ternary
                  content={this.props.products.sort((a, b) => ((a.name < b.name) ? -1 : (a.name > b.name) ? 1 : 0)).map((product: ProductModel) => (
                    { value: String(product.id), label: product.name }
                  ))}
                  selected={this.getSelectedProductDropDownValue(selectedProduct)}
                  handleChange={this.handleProductChange}
                  label={t('l.rate_plans.product')}
                  filterFields={[ 'value', 'label' ]}
                />
              </div>
              <div className="row align-items-center ml-2 rates-dropdown-element-container">
                <DropDownWithLabel
                  label={t('l.rate_plans.desk_group')}
                  // eslint-disable-next-line no-nested-ternary
                  content={this.props.deskGroups.sort((a, b) => ((a.name < b.name) ? -1 : (a.name > b.name) ? 1 : 0)).map((deskGroup: DeskGroupModel) => (
                    { value: String(deskGroup.id), label: deskGroup.name }
                  ))}
                  selected={this.state.selectedDeskGroup}
                  handleChange={this.handleDeskGroupChange}
                  filterFields={[ 'value', 'label' ]}
                />
              </div>
              <div className="row align-items-center ml-2 rates-dropdown-element-container">
                <div className="d-flex flex-column justify-content-start rates-input">
                  <label className="input-with-label-label">Car classes</label>
                  <div className="input-with-label d-flex align-items-center">
                    <input
                      value={this.state.selectedAcrissCodes.join(',')}
                      onChange={(v) => {
                        this.setState({ selectedAcrissCodes: v.target.value.replace(/\s+/g, ',').toUpperCase().split(',') });
                      }}
                      onKeyUp={(k) => k.key === 'Enter' && this.handleCarClassChange()}
                      onBlur={() => this.handleCarClassChange()}
                      type="text"
                      name="input-input"
                      className={'input-toggle input-input filter'}
                      autoComplete={'off'}
                      id="input-input"
                      placeholder="Type to filter"
                    />
                    <div className="input-clear" onClick={() => this.onCarClassClear()}>
                    </div>
                  </div>
                </div>
              </div>
              <div className="d-flex flex-column ml-3">
                <a href="#currentDateLine" className="btn btn-text-only">{t('b.rate_plans.scroll_to_current_month')}</a>
              </div>
            </div>
            {this.props.rateCacheStatus && (
              <div className="d-flex flex-column justify-content-center align-items-center mr-1">
                <CacheStatusLabel onPublish={this.onPublish} status={this.props.rateCacheStatus}
                                  canPublish={this.props.coreCacheStatus ? this.props.coreCacheStatus.unPublishedChangesFound : undefined}/>
              </div>
            )}
          </div>
        </div>
        {this.props.ratePlans.length !== 0 ? (
          <div className="timeline-container timeline-table">
            <RatesTimeline
              selectedRatePlan={this.state.selectedRatePlan}
              ratePlansSortedByDesk={this.getPlansToBeRendered()}
              onTimelineObjectClick={(id: number) => this.onTimelineObjectClick(id)}
              startDate={dayjs().startOf('day').format('YYYY-MM')}
              endDate={this.getEndDate()}
              cacheUpdatedStarted={this.props.coreCacheStatus ? this.props.coreCacheStatus.updateStartedAt : undefined}
            />
          </div>
        ) : (
          <div className="temp-container">
            <div>{t('text.rate_plans.no_rate_plans_found')}</div>
          </div>
        )}
        <div className="page-content">
          {(!this.state.isRateTableOpen && this.props.ratePlans.length !== 0) && (
            <div className="temp-container">
              {t('text.rate_plans.click_on_rate_or_adjustment')}
            </div>
          )}
          {this.state.selectedRatePlan && this.state.isRateTableOpen && (
            <>
              <div className="row d-flex justify-content-between align-items-center mt-2 ml-2">
                <div className="row align-items-center">
                  {this.props.selectedRatePlan && (
                    <>
                      <h1 className="rates-detail-header-label mr-2">
                        {`${this.props.selectedRatePlan.name} (ID: ${this.props.selectedRatePlan.id})`}
                      </h1>
                      <label
                        className="margin-right-medium rates-detail-header-date">{`${dayjs(this.props.selectedRatePlan.validFrom).format(DATE_FORMAT)} - ${dayjs(this.props.selectedRatePlan.validTo).format(DATE_FORMAT)}`}</label>
                    </>
                  )}
                </div>
                <div className="row mr-2 align-items-center">
                  <button type="button" className="btn btn-fixed-wide btn-outline-secondary ml-1"
                          onClick={() => this.setState({ isDeleteModalOpen: true })}>
                    {t('b.delete_Generic delete button').toUpperCase()}</button>
                  {this.props.selectedRatePlan && this.props.selectedRatePlan.statusType === StatusType.ACTIVE && (
                    <button type="button" className="btn btn-outline-secondary ml-1"
                            onClick={() => this.setState({ isConfirmDisableModalOpen: true })}>
                      {t('b.rate_plans.deactivate').toUpperCase()}
                    </button>
                  )}
                  {this.props.selectedRatePlan && this.props.selectedRatePlan.statusType === StatusType.INACTIVE && (
                    <button type="button" className="btn btn-outline-secondary  ml-1"
                            onClick={() => this.activateRatePlanClicked()}>
                      {t('b.rate_plans.activate').toUpperCase()}</button>
                  )}
                  <Link
                    to={{
                      pathname: `/rates/${this.props.selectedRatePlan && this.props.selectedRatePlan.rateType === RateType.BASE_RATE ? 'standardcopy' : 'adjustmentcopy'}/${this.props.selectedRatePlan ? this.props.selectedRatePlan.id : '0'}/view`
                    }}
                    className="btn btn-primary btn-fixed-wide ml-1"
                  >
                    {t('b.duplicate').toUpperCase()}
                  </Link>
                  <Link
                    to={{
                      pathname: `/rates/${this.props.selectedRatePlan && this.props.selectedRatePlan.rateType === RateType.BASE_RATE ? 'standardrate' : 'adjustment'}/${this.props.selectedRatePlan ? this.props.selectedRatePlan.id : '0'}/view`
                    }}
                    className="btn btn-primary btn-fixed-wide ml-1"
                  >
                    {t('b.edit_Generic edit button').toUpperCase()}
                  </Link>
                </div>
              </div>
              <div className="d-flex ml-1 justify-content-start align-items-center mb-2">
                <div className="mr-2 d-flex">
                  <div className="mr-1">{t('l.rate_plans.status')}</div>
                  {this.props.selectedRatePlan && this.props.selectedRatePlan.statusType === StatusType.ACTIVE && (
                    <div className="text-success">{t('status_text.rate_plans.active')}</div>
                  )}
                  {this.props.selectedRatePlan && this.props.selectedRatePlan.statusType === StatusType.INACTIVE && (
                    <div className="text-DANGEr">{t('status_text.rate_plans.inactive')}</div>
                  )}
                </div>
                <div className="mr-2 vertical-dividing-line"/>
                <div className="mr-2 d-flex">
                  <div className="mr-1">{t('l.rate_plans.cache')}</div>
                  {this.props.coreCacheStatus && this.props.selectedRatePlan && dayjs(this.props.coreCacheStatus.updateStartedAt).isAfter(dayjs(this.props.selectedRatePlan.modifiedAt)) && (
                    <div className="text-success">{t('status_text.rate_plans.up_to_date')}</div>
                  )}
                  {this.props.coreCacheStatus && this.props.selectedRatePlan && dayjs(this.props.coreCacheStatus.updateStartedAt).isBefore(dayjs(this.props.selectedRatePlan.modifiedAt)) && (
                    <div
                      className="text-danger">{t('status_text.rate_plans.will_update_in', { time: getTimeBeforePublish() })}</div>
                  )}
                </div>
                {this.props.selectedRatePlan?.modifiedAt && (
                  <>
                    <div className="mr-2 vertical-dividing-line"/>
                    <div>{`${this.props.selectedRatePlan.modifiedBy ? t('status_text.rate_plans.modified_by_at', { person: this.props.selectedRatePlan.modifiedBy }) : t('status_text.rate_plans.modified_at')} ${dayjs(this.props.selectedRatePlan.modifiedAt).format(DATE_TIME_FORMAT)}`}</div>
                  </>
                )}
              </div>
              <div className="mt-1">
                {this.state.selectedRatePlan && this.props.ratePlanPeriods && (
                  <RatePlanDetailTableView
                    selectedRatePlan={this.state.selectedRatePlan}
                    carModels={this.props.carModels}
                    ratePlanPeriods={this.props.ratePlanPeriods}
                  />
                )}
              </div>
            </>
          )}
        </div>
        <ConfirmationModal
          title={t('modal_text.rate_plans.confirm_removal_title')}
          content={t('modal_text.rate_plans.confirm_removal_content')}
          isOpen={this.state.isDeleteModalOpen}
          toggle={() => this.setState({ isDeleteModalOpen: false })}
          onConfirm={() => this.onDeleteConfirm()}
          onCancel={() => this.setState({ isDeleteModalOpen: false })}
        />
        <ConfirmationModal
          title={t('modal_text.rate_plans.confirm_disable_title')}
          content={t('modal_text.rate_plans.confirm_disable_content')}
          isOpen={this.state.isConfirmDisableModalOpen}
          toggle={() => this.setState({ isConfirmDisableModalOpen: false })}
          onConfirm={() => this.onDisableConfirm()}
          onCancel={() => this.setState({ isConfirmDisableModalOpen: false })}
        />
        <RatePlanDurationModal
          toggle={() => this.setState({ isDurationAdjustmentModalOpen: false })}
          isOpen={this.state.isDurationAdjustmentModalOpen}
          precedingPlan={this.state.precedingPlan}
          followingPlan={this.state.followingPlan}
          onPrecedingPlanExtend={(id: number) => this.onPrecedingPlanExtend(id)}
          onFollowingPlanExtend={(id: number) => this.onFollowingPlanExtend(id)}
        />
      </main>
    );
  }
}

const mapStateToProps = (state: AppState) => ({
  selectedRatePlan: state.rates.ratePlans.selectedRatePlan,
  ratePlans: state.rates.ratePlans.ratePlans,
  products: state.setup.products.products,
  deskGroups: state.setup.deskGroups.deskGroups,
  carModels: state.setup.carModels.carModels,
  ratePlanPeriods: state.core.ratePlanPeriods.ratePlanPeriods,
  user: state.authentication.user,
  storedProductId: state.rates.ratePlans.productId,
  storedDeskGroupId: state.rates.ratePlans.deskGroupId,
  storedAcrissCodes: state.rates.ratePlans.acrissCodes,
  rateCacheStatus: state.rates.rateCacheStatus.rateCacheStatus,
  coreCacheStatus: state.rates.rateCacheStatus.coreCacheStatus,
  carClasses: state.core.carClasses.acrissCodes
});

const mapDispatchToProps = (dispatch: ThunkDispatch<{}, {}, RatePlanActionTypes>) => ({
  getRatePlanList: (productId?: number, deskGroupId?: number, carClasses?: string[]) => dispatch(getRatePlanList(productId, deskGroupId, carClasses)),
  getProductList: () => dispatch(getProductList()),
  getDeskGroupList: () => dispatch(getDeskGroupList()),
  getCarModelList: () => dispatch(getCarModelsList()),
  getAllCarClasses: () => dispatch(getAllCarClasses()),
  getRatePlanPeriodsList: () => dispatch(getRatePlanPeriods()),
  getOneRatePlan: (id: number) => dispatch(getRatePlan(id)),
  deleteRatePlan: (id: number) => dispatch(deleteRatePlan(id)),
  patchRatePlan: (id: number, request: RatePlanPatchRequest) => dispatch(patchRatePlan(id, request)),
  saveProductId: (id: number) => dispatch(saveProductId(id)),
  saveDeskGroupId: (id: number) => dispatch(saveDeskGroupId(id)),
  saveAcrissCodes: (acrissCodes: string[]) => dispatch(saveAcrissCodes(acrissCodes)),
  getRatePlansView: (id?: number) => dispatch(getRatePlansView(id)),
  getRateCacheStatus: () => dispatch(getRateCacheStatus()),
  getCoreCacheStatus: () => dispatch(getCoreCacheStatus())
});

export default withTranslation()(connect(
  mapStateToProps,
  mapDispatchToProps
)(RatePlans));
